import { useQuery } from "@tanstack/react-query";
import { Link, SearchParamError, useRouter, useRouterState } from "@tanstack/react-router";
import { MessagePage } from "../components/MessagePage";
import { ApiCodedError, ApiError401, api } from "../lib/api";

type RouteErrorProps = {
	error: unknown;
	reset?: () => void;
};

/**
 * Router-wide error boundary. Wired up as `defaultErrorComponent`, so every
 * route gets a way out instead of the raw error dump TanStack renders by
 * default — which on a home-screen/standalone launch is a dead end, because
 * there is no browser back button to escape with.
 */
export default function RouteError({ error, reset }: RouteErrorProps) {
	const router = useRouter();

	// A SearchParamError means the URL itself is incomplete — typically a
	// bookmark or home-screen shortcut for /resultat or /year-results that lost
	// its ?lat=&lon= query string. Nothing to retry; the user needs to pick a
	// location again.
	const isMissingParams = error instanceof SearchParamError;
	// The full-year page needs a login and a year-access purchase for the place;
	// the server says which is missing.
	const needsLogin = error instanceof ApiError401;
	const needsPurchase = error instanceof ApiCodedError && error.code === "year_access_required";
	// Free day calculations are capped per visitor; waiting a minute fixes it.
	const rateLimited = error instanceof ApiCodedError && error.code === "rate_limited";
	const details = error instanceof Error ? error.message : String(error);
	const search = useRouterState({ select: (s) => s.location.search }) as {
		lat?: number;
		lon?: number;
		year?: number;
		addr?: string;
	};

	// Guest buyers have an account (made from their Stripe email) but no password.
	// Whether they can get in on this device depends on magic-link email.
	const { data: billing } = useQuery({
		queryKey: ["billing-config"],
		queryFn: () => api.getStripeConfig(),
		enabled: needsLogin,
		staleTime: Number.POSITIVE_INFINITY,
	});
	const guestHelp = billing?.emailLogin
		? "Kjøpte du uten å logge inn? Da fikk du en konto på e-postadressen du betalte med. Trykk «Logg inn» og be om en innloggingslenke på e-post."
		: "Kjøpte du uten å logge inn? Da ligger tilgangen i nettleseren du betalte i. Åpne siden der, eller ta kontakt så hjelper vi deg inn.";

	const retry = () => {
		reset?.();
		router.invalidate();
	};

	const { title, body } = needsLogin
		? {
				title: "Logg inn for å se hele året",
				body: "Soltidene for hele året hører til kontoen som kjøpte stedet. Logg inn, så finner du stedene dine under «Mine steder».",
			}
		: needsPurchase
			? {
					title: "Du har ikke tilgang til dette stedet",
					body: "Kontoen din har ikke kjøpt dette stedet. Ett kjøp gir soltidene for alle dager, alle år.",
				}
			: rateLimited
				? {
						title: "Litt for mange beregninger",
						body: "Det har kommet mange beregninger fra nettverket ditt på kort tid. Vent et minutt og prøv igjen.",
					}
				: isMissingParams
					? {
							title: "Vi mangler posisjonen",
							body: "Lenken du kom inn på har ikke med seg stedet. Velg stedet på nytt fra forsiden, så henter vi soltidene igjen.",
						}
					: {
							title: "Noe gikk galt",
							body: "Siden kunne ikke vises. Prøv på nytt, eller start beregningen fra forsiden.",
						};

	return (
		<MessagePage
			title={title}
			actions={
				<>
					{needsLogin && (
						<Link to="/login" className="btn-primary">
							Logg inn
						</Link>
					)}
					{needsPurchase && (
						<Link
							to="/pricing"
							search={{ lat: search.lat, lon: search.lon, year: search.year, addr: search.addr }}
							className="btn-primary"
						>
							Kjøp tilgang
						</Link>
					)}
					{!isMissingParams && !needsLogin && !needsPurchase && (
						<button type="button" onClick={retry} className="btn-primary">
							Prøv igjen
						</button>
					)}
					<Link to="/" className={isMissingParams ? "btn-primary" : "btn-secondary"}>
						Til forsiden
					</Link>
				</>
			}
			aside={
				<details>
					<summary className="cursor-pointer text-sm text-faint hover:text-muted">Tekniske detaljer</summary>
					<pre className="mt-3 max-h-48 overflow-auto rounded-lg bg-sunken p-3 text-xs leading-relaxed whitespace-pre-wrap text-muted">
						{details}
					</pre>
				</details>
			}
		>
			<p>{body}</p>
			{needsLogin && <p>{guestHelp}</p>}
		</MessagePage>
	);
}
